import { DataTableBodyCellProps as BaseDataTableBodyCellProps } from "../../../base/cells/TableBodyCell";
import { Cell } from "@tanstack/react-table";
import { DataTableBodyMutableCell } from "./DataTableBodyMutableCell";
import { DataTableBodyImmutableCell } from "./DataTableBodyImmutableCell";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { ReactNode } from "react";
import { CellPosition } from "../../../internal/types";

export type DataTableBodyCellProps<T> = Omit<
  BaseDataTableBodyCellProps,
  "onChange"
> & {
  cellPosition: CellPosition;
  cell: Cell<T, unknown>;
  children: ReactNode;
  isHovered: boolean;
};

/**
 * Renders a body cell in a DataTable, delegating to either a mutable (editable) or immutable cell
 * based on the column's editMode configuration.
 *
 * @param props - The props for the DataTableBodyCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to render in the cell
 * @param props.isHovered - Whether the cell is currently hovered
 * @returns The rendered cell component
 */
export function DataTableBodyCell<T>(props: DataTableBodyCellProps<T>) {
  const { cell, cellPosition, children, isHovered, ...rest } = props;

  const column = cell.column;
  const editMode = column.columnDef.meta?.editMode;

  const Component = editMode
    ? DataTableBodyMutableCell<T>
    : DataTableBodyImmutableCell<T>;
  return (
    <Component
      cell={cell}
      cellPosition={cellPosition}
      {...rest}
      data-cell-pos={formatCellPositionString(cellPosition)}
      isHovered={isHovered}
    >
      {children}
    </Component>
  );
}
